import React, { useState, useEffect, useRef } from 'react';
import { Leaf, Heart, Globe, Shield } from 'lucide-react';

const stats = [
  { value: 12, suffix: '', label: 'Albanian Destinations' },
  { value: 450, suffix: 'km', label: 'of Coastline' },
  { value: 6, suffix: '', label: 'Signature Experiences' },
  { value: 24, suffix: '/7', label: 'Concierge Service' },
];


const values = [
  {
    icon: Heart,
    title: 'Authentic Albania',
    description: 'We go beyond the surface to reveal the real Albania: its warmth, traditions, and hidden treasures known only to locals.',

  },
  {
    icon: Leaf,
    title: 'Sustainable Tourism',
    description: 'We partner with local communities and eco-conscious properties to protect Albania\'s pristine landscapes for generations.',
  },
  {
    icon: Globe,
    title: 'Cultural Immersion',
    description: 'From Ottoman heritage cities to alpine villages, we honor Albania\'s rich tapestry of cultures and centuries of history.',
  },
  {
    icon: Shield,
    title: 'Bespoke Journeys',
    description: 'Every itinerary is handcrafted to your desires, whether you seek Riviera relaxation, mountain adventure, or cultural discovery.',

  },
];

const AnimatedCounter: React.FC<{ target: number; suffix: string; isVisible: boolean }> = ({
  target,
  suffix,
  isVisible,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 2000;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const AboutSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 lg:py-32 bg-[#FAF8F3]" ref={sectionRef}>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Main About */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-24">
          {/* Image Side */}
          <div className="relative">
            <div className="img-zoom aspect-[4/5]">
              <img
                src="https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1774977388422_c59b90bb.jpg"
                alt="Zera Travel luxury Albania journey: curated Albanian Riviera, boutique hotels, private villas and bespoke Mediterranean experiences"

                className="w-full h-full object-cover"
                loading="lazy"
              />

            </div>
            {/* Floating accent card */}
            <div className="absolute -bottom-8 -right-4 lg:-right-8 bg-[#1A3B5C] p-8 max-w-[240px]">
              <span className="font-serif text-4xl text-[#C9A961] block mb-2">Albania</span>
              <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/70">
                Europe's Last Undiscovered Gem
              </span>
            </div>
          </div>

          {/* Text Side */}
          <div>
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
              Our Philosophy
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A3B5C] font-light mb-8 leading-tight">
              The Art of<br />Transformative Travel
            </h2>
            <div className="space-y-4 mb-10">
              <p className="font-sans text-sm text-[#666] leading-relaxed">
                At Zera, we believe that travel at its finest is not merely about destinations; it is about the profound transformation that occurs when extraordinary places meet extraordinary care.

              </p>
              <p className="font-sans text-sm text-[#666] leading-relaxed">
                Founded on the principle that luxury should be both deeply personal and environmentally conscious, we curate experiences that honor the natural beauty and cultural richness of every destination we touch.
              </p>
              <p className="font-sans text-sm text-[#666] leading-relaxed">
                Our dedicated concierge team works tirelessly to anticipate your every need, crafting bespoke itineraries that reflect your unique vision of the perfect journey.
              </p>
            </div>
          </div>
        </div>



        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24 py-12 border-y border-[#E8DFD0]">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <span className="font-serif text-4xl md:text-5xl text-[#1A3B5C] block mb-2">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} isVisible={isVisible} />
              </span>
              <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#999]">
                {stat.label}
              </span>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="text-center mb-14">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            What Guides Us
          </span>
          <h3 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light gold-underline">
            Our Values
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((val) => (
            <div key={val.title} className="text-center group">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#C9A961]/30 flex items-center justify-center group-hover:bg-[#1A3B5C] group-hover:border-[#1A3B5C] transition-all duration-300">
                <val.icon className="w-6 h-6 text-[#C9A961] group-hover:text-white transition-colors duration-300" />
              </div>
              <h4 className="font-serif text-xl text-[#1A3B5C] mb-3">{val.title}</h4>
              <p className="font-sans text-xs text-[#888] leading-relaxed">{val.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
